import * as XLSX from 'xlsx';

export type ExportType = 'table' | 'data';

export interface ExportFilters {
  selectedCreationYears: Set<number>;
  selectedArrivalYears: Set<number>;
  selectedManagers: Set<string>;
  selectedSegments: Set<string>;
  selectedClients: Set<string>;
  selectedHotels: Set<string>;
  selectedHotelGroups: Set<string>;
  selectedArrivalMonths: Set<number>;
  selectedArrivalQuarters: Set<number>;
  selectedCreationMonths: Set<number>;
  selectedCreationQuarters: Set<number>;
  allClientsSelected: boolean;
  allHotelsSelected: boolean;
  allHotelGroupsSelected: boolean;
}

export interface ExportOptions {
  filename: string;
  includeMetadata?: boolean;
  format: 'xlsx' | 'csv';
  exportType?: ExportType;
  tableDataTransformer?: () => any[];
}

// Helper function to check if a filter is in 'all' state
function isAllSelected<T>(selected: Set<T>, allOptions: T[]): boolean {
  return selected.size === 0 || selected.size === allOptions.length;
}

// Apply the same filtering logic as used in the reports
export function applyFilters(data: any[], filters: ExportFilters): any[] {
  return data.filter(row => {
    // Manager filter (special case - empty set means "no filter")
    if (filters.selectedManagers.size > 0 && !filters.selectedManagers.has(row.request_manager)) {
      return false;
    }
    
    // Client filter
    if (!filters.allClientsSelected && !filters.selectedClients.has(row.company)) {
      return false;
    }
    
    // Arrival year filter
    if (filters.selectedArrivalYears.size > 0 && !filters.selectedArrivalYears.has(row.arrival_year)) {
      return false;
    }
    
    // Creation year filter
    if (filters.selectedCreationYears.size > 0 && !filters.selectedCreationYears.has(row.request_year)) {
      return false;
    }
    
    // Segment filter
    if (filters.selectedSegments.size > 0 && !filters.selectedSegments.has(row.segment)) {
      return false;
    }
    
    // Hotel group filter
    const groupName = row.hotel_group_name ? row.hotel_group_name : 'No Group';
    if (!filters.allHotelGroupsSelected && !filters.selectedHotelGroups.has(groupName)) {
      return false;
    }
    
    // Hotel filter
    if (!filters.allHotelsSelected && !filters.selectedHotels.has(row.venue)) {
      return false;
    }
    
    // Arrival month filter
    if (filters.selectedArrivalMonths.size > 0 && !filters.selectedArrivalMonths.has(row.arrival_month)) {
      return false;
    }
    
    // Arrival quarter filter
    if (filters.selectedArrivalQuarters.size > 0 && !filters.selectedArrivalQuarters.has(row.arrival_quarter)) {
      return false;
    }
    
    // Creation month filter
    if (filters.selectedCreationMonths.size > 0 && !filters.selectedCreationMonths.has(row.request_month)) {
      return false;
    }
    
    // Creation quarter filter
    if (filters.selectedCreationQuarters.size > 0 && !filters.selectedCreationQuarters.has(row.request_quarter)) {
      return false;
    }
    
    return true;
  });
}

// Transform data for export
export function transformDataForExport(data: any[]): any[] {
  return data.map(row => ({
    request_id: row.request_id,
    client_id: row.client_id,
    company_id: row.company_id,
    team_member_id: row.team_member_id,
    request_manager: row.request_manager,
    company: row.company,
    venue: row.venue,
    hotel_group_name: row.hotel_group_name || 'No Group',
    segment: row.segment,
    status: row.status,
    destination_city: row.destination_city,
    destination_country: row.destination_country,
    checkin_date: row.checkin_date,
    checkout_date: row.checkout_date,
    arrival_year: row.arrival_year,
    arrival_quarter: row.arrival_quarter,
    arrival_month: row.arrival_month,
    request_year: row.request_year,
    request_quarter: row.request_quarter,
    request_month: row.request_month,
    number_of_nights: row.number_of_nights,
    half_twin_qty: row.half_twin_qty,
    supl_single_qty: row.supl_single_qty,
    double_gs_qty: row.double_gs_qty,
    supl_triple_qty: row.supl_triple_qty,
    quart_quad_qty: row.quart_quad_qty,
    total_number_of_rooms: row.total_number_of_rooms,
    estimated_revenue: row.estimated_revenue,
    signed_revenue: row.signed_revenue,
    realised_revenue: row.realised_revenue,
    price: row.price,
    client_request_reference: row.client_request_reference,
    request_type: row.request_type,
    source: row.source,
    currency: row.currency
  }));
}

// Generate filter metadata for export
export function generateFilterMetadata(filters: ExportFilters): any {
  const metadata = {
    'Export Date': new Date().toISOString(),
    'Total Records': 0, // Will be filled by caller
    'Filters Applied': {} as Record<string, string>
  };

  // Add filter information
  if (filters.selectedManagers.size > 0) {
    metadata['Filters Applied']['Managers'] = Array.from(filters.selectedManagers).join(', ');
  }
  
  if (!filters.allClientsSelected && filters.selectedClients.size > 0) {
    metadata['Filters Applied']['Clients'] = Array.from(filters.selectedClients).join(', ');
  }
  
  if (filters.selectedArrivalYears.size > 0) {
    metadata['Filters Applied']['Arrival Years'] = Array.from(filters.selectedArrivalYears).sort().join(', ');
  }
  
  if (filters.selectedCreationYears.size > 0) {
    metadata['Filters Applied']['Creation Years'] = Array.from(filters.selectedCreationYears).sort().join(', ');
  }
  
  if (filters.selectedSegments.size > 0) {
    metadata['Filters Applied']['Segments'] = Array.from(filters.selectedSegments).join(', ');
  }
  
  if (!filters.allHotelGroupsSelected && filters.selectedHotelGroups.size > 0) {
    metadata['Filters Applied']['Hotel Groups'] = Array.from(filters.selectedHotelGroups).join(', ');
  }
  
  if (!filters.allHotelsSelected && filters.selectedHotels.size > 0) {
    metadata['Filters Applied']['Hotels'] = Array.from(filters.selectedHotels).join(', ');
  }
  
  if (filters.selectedArrivalMonths.size > 0 && filters.selectedArrivalMonths.size < 12) {
    metadata['Filters Applied']['Arrival Months'] = Array.from(filters.selectedArrivalMonths).sort().join(', ');
  }
  
  if (filters.selectedArrivalQuarters.size > 0 && filters.selectedArrivalQuarters.size < 4) {
    metadata['Filters Applied']['Arrival Quarters'] = Array.from(filters.selectedArrivalQuarters).sort().join(', ');
  }
  
  if (filters.selectedCreationMonths.size > 0 && filters.selectedCreationMonths.size < 12) {
    metadata['Filters Applied']['Creation Months'] = Array.from(filters.selectedCreationMonths).sort().join(', ');
  }
  
  if (filters.selectedCreationQuarters.size > 0 && filters.selectedCreationQuarters.size < 4) {
    metadata['Filters Applied']['Creation Quarters'] = Array.from(filters.selectedCreationQuarters).sort().join(', ');
  }

  return metadata;
}

// Export to Excel
export function exportToExcel(
  data: any[], 
  filters: ExportFilters, 
  options: ExportOptions
): void {
  let transformedData: any[];
  
  // Use table transformer if exportType is 'table' and transformer is provided
  if (options.exportType === 'table' && options.tableDataTransformer) {
    transformedData = options.tableDataTransformer();
  } else {
    // Default behavior for 'data' export
    const filteredData = applyFilters(data, filters);
    transformedData = transformDataForExport(filteredData);
  }
  
  if (transformedData.length === 0) {
    alert('No data to export with the current filters.');
    return;
  }

  const workbook = XLSX.utils.book_new();
  
  // Add main data sheet
  const worksheet = XLSX.utils.json_to_sheet(transformedData);
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
  
  // Add metadata sheet if requested
  // For table exports, only add metadata if explicitly requested
  // For data exports, follow includeMetadata flag
  if (options.includeMetadata) {
    const metadata = generateFilterMetadata(filters);
    metadata['Total Records'] = transformedData.length;
    
    const metadataSheet = XLSX.utils.json_to_sheet([metadata]);
    XLSX.utils.book_append_sheet(workbook, metadataSheet, 'Export Info');
  }
  
  // Generate filename with timestamp
  const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
  const filename = `${options.filename}_${timestamp}.xlsx`;
  
  XLSX.writeFile(workbook, filename);
}

// Export to CSV
export function exportToCSV(
  data: any[], 
  filters: ExportFilters, 
  options: ExportOptions
): void {
  let transformedData: any[];
  
  // Use table transformer if exportType is 'table' and transformer is provided
  if (options.exportType === 'table' && options.tableDataTransformer) {
    transformedData = options.tableDataTransformer();
  } else {
    // Default behavior for 'data' export
    const filteredData = applyFilters(data, filters);
    transformedData = transformDataForExport(filteredData);
  }
  
  if (transformedData.length === 0) {
    alert('No data to export with the current filters.');
    return;
  }

  // Convert to CSV
  const csv = XLSX.utils.sheet_to_csv(XLSX.utils.json_to_sheet(transformedData));
  
  // Add metadata as comments at the top if requested
  // For table exports (exportType === 'table'), do NOT include metadata in CSV
  // For data exports, follow includeMetadata flag
  let csvContent = csv;
  if (options.exportType !== 'table' && options.includeMetadata) {
    const metadata = generateFilterMetadata(filters);
    metadata['Total Records'] = transformedData.length;
    
    const metadataLines = Object.entries(metadata).map(([key, value]) => {
      if (typeof value === 'object' && value !== null) {
        return `# ${key}: ${Object.entries(value).map(([k, v]) => `${k}=${v}`).join(', ')}`;
      }
      return `# ${key}: ${value}`;
    });
    
    csvContent = metadataLines.join('\n') + '\n' + csv;
  }
  
  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  
  const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
  const filename = `${options.filename}_${timestamp}.csv`;
  link.setAttribute('download', filename);
  
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// Main export function
export function exportData(
  data: any[], 
  filters: ExportFilters, 
  options: ExportOptions
): void {
  if (options.format === 'xlsx') {
    exportToExcel(data, filters, options);
  } else {
    exportToCSV(data, filters, options);
  }
}
